import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import { createElement, useMemo, useState } from "react";
import { trackEvent } from "@/services/analytics-adapter";
import { publicFlowOutlinedInputRootStyles } from "@/theme";
import { useTranslation } from "react-i18next";
import FaceIconBrandLarge from "@/theme/icons/FaceIconBrandLarge";
import QuestionTypeSexAtBirth from "@/features/Common/components/QuestionTypeSexAtBirth";
import QuestionTypeWeight from "@/features/Common/components/QuestionTypeWeight";
import QuestionTypeHeight from "@/features/Common/components/QuestionTypeHeight";
import { HeightUnit } from "@/features/Common/components/HeightInput";
import QuestionTypeSmokingStatus from "@/features/Common/components/QuestionTypeSmokingStatus";
import { Gender } from "../../../../types";
import CollectUserPersonalInformation from "../CollectUserPersonalInformation";
import {
  UserType,
  QuestionTypeName,
  QuestionType,
  SKIPPABLE_STEPS,
  getFlowForUserType,
} from "../../index";
import UserCreatedScreen from "../UserCreatedScreen";
import { submitPublicFaceScanData } from "@/services/core-api-adapter";
import DefaultError from "@/features/Common/components/DefaultError";

interface PreFaceScanFlowProps {
  stepIndex: number;
  onNextStep: () => void;
  onSkipStep: () => void;
  onComplete: Function;
  userType: UserType | null;
  onComeBackLater?: () => void;
  onSubmitErrorChange?: (isError: boolean) => void;
}

interface QuestionTypesMetadata {
  [key: string]: {
    questionType: QuestionTypeName;
    component: any;
    onChange?: (value: any) => void;
    onCompletedFlow?: () => void;
    extraProps?: Record<string, any>;
  };
}

export default function PreFaceScanFlow({
  stepIndex,
  onNextStep,
  onSkipStep,
  onComplete,
  userType,
  onComeBackLater,
  onSubmitErrorChange,
}: PreFaceScanFlowProps) {
  const { t } = useTranslation();

  const flow = getFlowForUserType(userType);
  const currentQuestionType = flow[stepIndex];
  const isSkippable = SKIPPABLE_STEPS.includes(currentQuestionType);
  const isLastStep = stepIndex === flow.length - 1;

  const [isValidInput, setIsValidInput] = useState(false);
  const [userDemographics, setUserDemographics] = useState<any>({
    recordType: userType,
  });
  const [generatedUserId, setGeneratedUserId] = useState<string | null>(null);
  const [isCreatingTestUser, setIsCreatingTestUser] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  // Memoize data objects to prevent infinite loops from new object references
  const personalInfoInitialData = useMemo(
    () => ({
      policyNumber: userDemographics.personalInformation?.policyNumber,
      dateOfBirth: userDemographics.dateOfBirth,
    }),
    [
      userDemographics.personalInformation?.policyNumber,
      userDemographics.dateOfBirth,
    ]
  );

  const heightData = useMemo(() => {
    const height = userDemographics.height;
    if (height === null || height === undefined) return undefined;
    if (typeof height === "object" && "value" in height && "unit" in height)
      return { value: String(height.value), unit: height.unit };
    return { value: String(height), unit: HeightUnit.CM };
  }, [userDemographics.height]);

  const weightData = useMemo(
    () =>
      userDemographics.weight ? { value: userDemographics.weight } : undefined,
    [userDemographics.weight]
  );

  const questionTypesMapping: QuestionTypesMetadata = {
    [QuestionType.PersonalInformation]: {
      questionType: QuestionType.PersonalInformation,
      component: CollectUserPersonalInformation,
      onChange: onPersonalInformationChange,
      extraProps: {
        initialData: personalInfoInitialData,
      },
    },
    [QuestionType.SexAtBirth]: {
      questionType: QuestionType.SexAtBirth,
      component: QuestionTypeSexAtBirth,
      onChange: onSexAtBirthChange,
      extraProps: {
        data: { value: userDemographics.sexAtBirth },
      },
    },
    [QuestionType.Height]: {
      questionType: QuestionType.Height,
      component: QuestionTypeHeight,
      onChange: onHeightChange,
      extraProps: {
        showSubtitle: false,
        data: heightData,
      },
    },
    [QuestionType.Weight]: {
      questionType: QuestionType.Weight,
      component: QuestionTypeWeight,
      onChange: onWeightChange,
      extraProps: {
        data: weightData,
      },
    },
    [QuestionType.SmokingStatus]: {
      questionType: QuestionType.SmokingStatus,
      component: QuestionTypeSmokingStatus,
      onChange: onSmokingStatusChange,
      extraProps: {
        data: { value: userDemographics.smokingStatus },
      },
    },
  };

  function getQuestionTypeComponent() {
    const { component, onChange, onCompletedFlow, extraProps } =
      questionTypesMapping[currentQuestionType];

    const props: any = {
      onChange: onChange,
      onCompletedFlow: onCompletedFlow,
      ...extraProps,
    };

    if (currentQuestionType === QuestionType.PersonalInformation) {
      props.userType = userType;
    }

    return createElement(component, props);
  }

  function onPersonalInformationChange(value: any) {
    setIsValidInput(value.isValid);

    if (value.isValid) {
      const { policyNumber, ...rest } = value.data;
      setUserDemographics({
        ...userDemographics,
        ...rest,
        ...(policyNumber !== undefined && {
          personalInformation: {
            ...userDemographics.personalInformation,
            policyNumber,
          },
        }),
      });

      setIsValidInput(value.isValid);
    }
  }

  function onSexAtBirthChange(value: any) {
    let gender = Gender.UNSPECIFIED;

    if (value.data.value) {
      gender = value.data.value;
      setUserDemographics({
        ...userDemographics,
        sexAtBirth: gender,
      });
      setIsValidInput(true);
    }
  }

  function onSmokingStatusChange(value: any) {
    if (value.isValid) {
      setUserDemographics({
        ...userDemographics,
        smokingStatus: value.data.value,
      });
      setIsValidInput(true);
    }
  }

  function onHeightChange(value: any) {
    if (!value.isValid) return;
    const heightFromInput = {
      value: Number(value.data.value),
      unit: (value.data.unit ?? HeightUnit.CM) as string,
    };
    const current = userDemographics.height;
    const currentValue =
      current != null && typeof current === "object" && "value" in current
        ? current.value
        : current;
    const currentUnit =
      current != null && typeof current === "object" && "unit" in current
        ? current.unit
        : HeightUnit.CM;
    if (
      currentValue !== heightFromInput.value ||
      currentUnit !== heightFromInput.unit
    ) {
      setUserDemographics({ ...userDemographics, height: heightFromInput });
    }
    setIsValidInput(true);
  }

  function onWeightChange(value: any) {
    if (value.isValid) {
      // Only update if value actually changed to prevent infinite loop
      if (userDemographics.weight !== value.data.value) {
        setUserDemographics({
          ...userDemographics,
          weight: value.data.value,
        });
      }
    }
    setIsValidInput(value.isValid);
  }

  const onContinue = async () => {
    setIsValidInput(false);

    trackEvent({
      event: "action.facePreScanContinue",
      source: "Public binah scan flow",
      questionType: currentQuestionType,
    });

    if (currentQuestionType === QuestionType.PersonalInformation) {
      setIsCreatingTestUser(true);
      setSubmitError(false);
      try {
        const response = await submitPublicFaceScanData({
          vitalSigns: null,
          userDemographics: userDemographics,
        });
        const policyNumber = (response as { policyNumber?: string })
          ?.policyNumber;
        if (policyNumber) {
          setGeneratedUserId(policyNumber);
          setUserDemographics({
            ...userDemographics,
            personalInformation: {
              ...userDemographics.personalInformation,
              policyNumber,
            },
          });
        }
        onNextStep();
      } catch (error) {
        console.error("Failed to submit public face scan data:", error);
        setSubmitError(true);
        onSubmitErrorChange?.(true);
      } finally {
        setIsCreatingTestUser(false);
      }
    } else if (currentQuestionType === QuestionType.UserCreated) {
      onNextStep();
    } else if (isLastStep) {
      onComplete(userDemographics);
    } else {
      onNextStep();
    }
  };

  if (currentQuestionType === QuestionType.UserCreated) {
    return (
      <UserCreatedScreen
        onContinue={onContinue}
        generatedUserId={generatedUserId}
      />
    );
  }

  if (submitError) {
    return (
      <DefaultError
        onTryAgain={() => {
          setSubmitError(false);
          onSubmitErrorChange?.(false);
        }}
        onComeBackLater={onComeBackLater}
      >
        <Typography variant="h2" pb={2}>
          {t("GenericError.title")}
        </Typography>
      </DefaultError>
    );
  }

  return (
    <Stack
      spacing={2}
      p={2}
      justifyContent="flex-end"
      sx={{
        height: "100%",
        // White background for form inputs in this flow (policy number, DOB, height, weight, etc.).
        // Done this way to avoid unnecessary extra props on shared components or changing theme defaults
        // globally since it only applies to this flow.
        ...publicFlowOutlinedInputRootStyles,
      }}
    >
      <FaceIconBrandLarge />

      {getQuestionTypeComponent()}

      <Stack spacing={1} direction="row" sx={{ width: "100%" }}>
        {isSkippable && (
          <Button
            fullWidth
            variant="outlined"
            onClick={(e) => {
              (e.currentTarget as HTMLButtonElement).blur();
              const fieldMap: Record<QuestionTypeName, string | null> = {
                [QuestionType.PersonalInformation]: null,
                [QuestionType.UserCreated]: null,
                [QuestionType.SexAtBirth]: "sexAtBirth",
                [QuestionType.Height]: "height",
                [QuestionType.Weight]: "weight",
                [QuestionType.SmokingStatus]: "smokingStatus",
              };
              const field = fieldMap[currentQuestionType];
              if (field) {
                setUserDemographics({
                  ...userDemographics,
                  [field]: null,
                });
              }
              if (isLastStep) {
                onComplete({
                  ...userDemographics,
                  ...(field ? { [field]: null } : {}),
                });
              } else {
                onSkipStep();
              }
            }}
          >
            {t("common.skipQuestion")}
          </Button>
        )}
        <Button
          fullWidth
          disabled={!isValidInput || isCreatingTestUser}
          onClick={onContinue}
        >
          {isCreatingTestUser ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("common.nextButton")
          )}
        </Button>
      </Stack>
    </Stack>
  );
}
